import type { ProfitItemData } from '@frontend/api';
import {
  HStack,
  IconButton,
  Td,
  Tr,
  Flex,
  Text,
  CloseIcon,
} from '@frontend/components';

import CostCategoryBadge from 'components/lib2/CostCategoryBadge';
import { CustomCategoryBadge } from 'components/lib2/CustomCategoryBadge';

import { COST_CATEGORY } from 'constants/all';

import FormattedInput from 'modules/quotes/quoteItems/components/InputComponents/InputCells/FormattedInputCell';
import Cell from 'modules/quotes/quoteItems/components/Table/Cell';

import { formatPercent, parseDecimalString } from 'utils/formatters';

export interface ProfitItemRowProps {
  profitItem: ProfitItemData;
  onRemove?: (id: string) => void; // defined for add/replace profit items; undefined for update profit items;
  onEdit: (id: string, data: Partial<ProfitItemData>) => void;
}

export const ProfitItemRow = ({
  profitItem,
  onEdit,
  onRemove,
}: ProfitItemRowProps) => {
  return (
    <Tr>
      <Td>
        <Flex alignItems="center">
          <HStack flex={1} justifyContent="space-between">
            <Text as="span" flex={1}>
              {profitItem.name}
            </Text>
            {profitItem.category === COST_CATEGORY.CUSTOM ? (
              <CustomCategoryBadge
                name={profitItem.customCategoryName}
                color={profitItem.customCategoryColor}
              />
            ) : (
              <CostCategoryBadge category={profitItem.category} />
            )}
          </HStack>
          {onRemove && (
            <IconButton
              ml={1}
              aria-label="Remove Pricing Item"
              onClick={() => onRemove(profitItem.id)}
            >
              <CloseIcon />
            </IconButton>
          )}
        </Flex>
      </Td>
      <Cell
        as={'td'}
        pl={0.25}
        display="table-cell"
        revertable={true}
        shouldHighlightOverrides={true}
        border="none"
        w="unset"
        h={3}
        editable
        editableComponent={FormattedInput}
        formatter={(v: string) =>
          v === ''
            ? onRemove
              ? '(Calculated values)'
              : '(Leave unchanged)'
            : formatPercent(v)
        }
        parser={(s) => {
          const parsedString = parseDecimalString(s);
          if (parsedString.at(0) === '-') {
            return parsedString.slice(1);
          } else {
            return parsedString;
          }
        }}
        calculatedKey="percentage"
        manualKey="manualPercentage"
        source={profitItem}
        onSave={(manualPercentage: string) =>
          onEdit(profitItem.id, { manualPercentage })
        }
      />
    </Tr>
  );
};
